import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, Send, Building2, CheckCircle2, Linkedin, Twitter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'

const inquiryTypes = [
  { value: '', label: 'Select Inquiry Type' },
  { value: 'general', label: 'General Inquiry' },
  { value: 'tender', label: 'Government Tender' },
  { value: 'project', label: 'Project Inquiry' },
  { value: 'partnership', label: 'Business Partnership' },
  { value: 'franchise', label: 'Franchise Application' },
  { value: 'vendor', label: 'Vendor Registration' },
  { value: 'careers', label: 'Career Inquiry' },
]

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    value: '+91 99231 00592',
    secondary: '+91 99608 54675',
    link: 'tel:+919923100592',
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'gsmcorpindia@gmail.com',
    link: 'mailto:gsmcorpindia@gmail.com',
  },
  {
    icon: MapPin,
    title: 'Head Office',
    value: 'FC Road, Pune, Maharashtra',
    secondary: 'India - 411004',
    link: '#',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    value: 'Mon - Sat: 9:00 AM - 6:00 PM',
    secondary: 'Sunday: Closed',
    link: '#',
  },
]

const offices = [
  {
    city: 'Pune (Head Office)',
    address: 'FC Road, Pune, Maharashtra',
    phone: '+91 99231 00592',
    email: 'gsmcorpindia@gmail.com',
  },
  {
    city: 'Mumbai (Regional)',
    address: 'Regional Office, Maharashtra',
    phone: '+91 99608 54675',
    email: 'gsmcorpindia@gmail.com',
  },
  {
    city: 'Nagpur (Regional)',
    address: 'Regional Office, Maharashtra',
    phone: '+91 99231 00592',
    email: 'gsmcorpindia@gmail.com',
  },
]

const socialLinks = [
  { icon: Linkedin, name: 'LinkedIn', href: '#' },
  { icon: Twitter, name: 'Twitter', href: '#' },
]

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))

    setIsSubmitting(false)
    setIsSuccess(true)

    // Reset after showing success
    setTimeout(() => {
      setIsSuccess(false)
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: '',
      })
    }, 3000)
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Building2 className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-graphite-300">Contact Us</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Let's <span className="gradient-text">Connect</span>
            </h1>
            <p className="text-xl text-graphite-300">
              Have a project in mind? We're here to help. Reach out to us and let's
              discuss how we can transform your institution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="container-premium">
          <div className="grid md:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <a href={info.link} className="block h-full">
                  <Card variant="hover" className="p-6 text-center group h-full">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{info.title}</h3>
                    <p className="text-graphite-300">{info.value}</p>
                    {info.secondary && (
                      <p className="text-graphite-500 text-sm mt-1">{info.secondary}</p>
                    )}
                  </Card>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8">
                <h2 className="heading-sm text-white mb-6">Send us a Message</h2>

                {isSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                    <p className="text-graphite-400">We'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        label="Full Name *"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                      <Input
                        label="Email Address *"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        label="Phone Number"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                      <Input
                        label="Company/Organization"
                        placeholder="Your company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>

                    <Select
                      label="Inquiry Type *"
                      options={inquiryTypes}
                      value={formData.inquiryType}
                      onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                      required
                    />

                    <Textarea
                      label="Message *"
                      placeholder="Tell us about your project or inquiry..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                    />

                    <Button type="submit" variant="gold" size="lg" className="w-full" loading={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      {!isSubmitting && <Send className="ml-2 w-4 h-4" />}
                    </Button>
                  </form>
                )}
              </Card>
            </motion.div>

            {/* Offices */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-sm text-white mb-6">Our Offices</h2>

              <div className="space-y-6 mb-8">
                {offices.map((office, index) => (
                  <Card key={index} variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-2">{office.city}</h3>
                    <p className="text-graphite-400 mb-2">{office.address}</p>
                    <div className="flex flex-col gap-2 text-sm">
                      <a href={`tel:${office.phone}`} className="flex items-center gap-2 text-primary-400 hover:text-primary-300">
                        <Phone className="w-4 h-4" />
                        <span>{office.phone}</span>
                      </a>
                      <a href={`mailto:${office.email}`} className="flex items-center gap-2 text-secondary-400 hover:text-secondary-300">
                        <Mail className="w-4 h-4" />
                        <span>{office.email}</span>
                      </a>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Social */}
              <Card variant="glass" className="p-6">
                <h3 className="text-white font-semibold mb-4">Connect With Us</h3>
                <div className="flex gap-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-graphite-400 hover:text-white hover:bg-primary-500 hover:border-primary-500 transition-all"
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </Card>

              {/* Map Placeholder */}
              <Card variant="glass" className="mt-6 aspect-video overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto text-primary-400 mb-2" />
                    <p className="text-graphite-400">Interactive Map</p>
                    <p className="text-sm text-graphite-500">FC Road, Pune, Maharashtra</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-secondary-600/20 to-accent-600/20" />
            <div className="relative">
              <h2 className="heading-md text-white mb-4">Ready to Start Your Project?</h2>
              <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
                Partner with GSM Corporation for comprehensive infrastructure solutions
                that drive growth and excellence. Let's discuss your requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <Link to="/services">
                    Explore Services
                    <Send className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/franchise">Become a Partner</Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  )
}